// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useAccountForm } from '@client/banks/hooks/use-account-form';
import type { BankAccount } from '@client/banks/types/account';
import { createEmptyBankAccount } from '@client/banks/utils/bank-form-defaults';
import {
  BANK_UPLOAD_CREATE_LINKED_ACCOUNT_VALUE,
  getLinkedLiquidityMode,
  toInstrumentIbanFromSelector,
} from '@client/banks/utils/linked-liquidity-selection';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { LinkedLiquiditySectionProps } from './linked-liquidity-section';

export type LinkedLiquiditySubmitResult =
  | { mode: 'none' | 'existing'; iban: string }
  | { mode: 'create'; account: BankAccount; iban: string }
  | { mode: 'invalid' };

export type LinkedLiquiditySectionApi = {
  resolveSubmit: () => Promise<LinkedLiquiditySubmitResult>;
  isCreateMode: boolean;
  isAccountFormValid: boolean;
};

export type LinkedLiquidityGate = {
  isCreateMode: boolean;
  isAccountFormValid: boolean;
};

export const useLinkedLiquiditySection = ({
  name = 'iban',
  currencyOptions,
  accountIbanOptions,
  defaultCurrency,
  labelI18nKey,
  apiRef,
  onGateChange,
}: LinkedLiquiditySectionProps) => {
  const { t } = useTranslation();
  const { setValue, getValues, control } = useFormContext<{
    iban: string;
    currency?: string;
  }>();

  const instrumentIban = useWatch({ control, name }) ?? '';
  const instrumentCurrency = useWatch({ control, name: 'currency' });

  const [selectorValue, setSelectorValue] = useState(() => instrumentIban);

  // Keep selector aligned when the parent form resets (add vs edit).
  useEffect(() => {
    setSelectorValue(current => {
      if (current === BANK_UPLOAD_CREATE_LINKED_ACCOUNT_VALUE) {
        if (!instrumentIban.trim()) {
          return current;
        }

        return instrumentIban;
      }

      return instrumentIban;
    });
  }, [instrumentIban]);

  const mode = useMemo(
    () => getLinkedLiquidityMode(selectorValue),
    [selectorValue],
  );

  const effectiveDefaultCurrency = useMemo(() => {
    const fromInstrument =
      typeof instrumentCurrency === 'string' ? instrumentCurrency.trim() : '';

    return fromInstrument || defaultCurrency;
  }, [defaultCurrency, instrumentCurrency]);

  const accountDefaultValues = useMemo(
    () => createEmptyBankAccount(effectiveDefaultCurrency),
    [effectiveDefaultCurrency],
  );

  const accountForm = useAccountForm({
    variant: 'createLinked',
    defaultValues: accountDefaultValues,
  });

  useEffect(() => {
    if (mode !== 'create') {
      return;
    }

    accountForm.reset(createEmptyBankAccount(effectiveDefaultCurrency));
    // Reset only when entering create or currency changes; omit accountForm identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [effectiveDefaultCurrency, mode]);

  const selectorOptions = useMemo(
    () => [
      ...accountIbanOptions,
      {
        value: BANK_UPLOAD_CREATE_LINKED_ACCOUNT_VALUE,
        label: t('aggregator.create-bank.linked-account-create-new', {
          ns: 'aggregator',
        }),
      },
    ],
    [accountIbanOptions, t],
  );

  const onSelectorChange = useCallback(
    (value: string) => {
      setSelectorValue(value);
      setValue(name, toInstrumentIbanFromSelector(value), {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [name, setValue],
  );

  const resolveSubmit =
    useCallback(async (): Promise<LinkedLiquiditySubmitResult> => {
      const currentMode = getLinkedLiquidityMode(selectorValue);

      if (currentMode === 'create') {
        const isValid = await accountForm.trigger();

        if (!isValid) {
          return { mode: 'invalid' };
        }

        const account = accountForm.getValues();
        const iban = account.iban.trim();

        return { mode: 'create', account: { ...account, iban }, iban };
      }

      const iban = getValues(name) ?? '';

      return {
        mode: currentMode,
        iban: toInstrumentIbanFromSelector(iban),
      };
    }, [accountForm, getValues, name, selectorValue]);

  const isCreateMode = mode === 'create';
  const isAccountFormValid = accountForm.formState.isValid;

  useEffect(() => {
    if (!apiRef) {
      return;
    }

    apiRef.current = {
      resolveSubmit,
      isCreateMode,
      isAccountFormValid,
    };

    return () => {
      apiRef.current = null;
    };
  }, [apiRef, isAccountFormValid, isCreateMode, resolveSubmit]);

  useEffect(() => {
    onGateChange?.({ isCreateMode, isAccountFormValid });
  }, [isAccountFormValid, isCreateMode, onGateChange]);

  return {
    t,
    selectorValue,
    selectorOptions,
    showAccountForm: isCreateMode,
    onSelectorChange,
    accountForm,
    currencyOptions,
    labelI18nKey,
    resolveSubmit,
    isCreateMode,
    isAccountFormValid,
  };
};
